import {
  ColumnDef as ReactTableColumnDef,
  SortingState as ReactTableSortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useEffect, useState } from 'react';

import { IS_SELECTED_COLUMN_ID } from './constants';
import { makeRowSelectionState } from './makeRowSelectionState';
import { SystemTPColumnKey, TPTableSortingState } from './types';

const getPinnedSystemColumnIds = (enableRowSelection: boolean): SystemTPColumnKey[] => [
  ...(enableRowSelection ? [IS_SELECTED_COLUMN_ID] : []),
];

type Props<T> = {
  data: T[];
  columns: (sortingState: TPTableSortingState) => ReactTableColumnDef<T, any>[];
  pinnedColumnIds?: string[];
  enableRowSelection: boolean;
  selectedRowIndices: number[] | undefined;
};

function useTPTableCore<T>({
  data,
  columns,
  pinnedColumnIds = [],
  enableRowSelection,
  selectedRowIndices = [],
}: Props<T>) {
  const [sorting, setSorting] = useState<ReactTableSortingState>([]);

  const sortingState: TPTableSortingState = Object.fromEntries(
    sorting.map((x) => [x.id, { desc: x.desc ?? false }]),
  );

  const table = useReactTable({
    data,
    columns: columns(sortingState),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    enablePinning: true,
    enableRowSelection,
    enableMultiRowSelection: false, // TODO: Allow multi-row selection; set to `false` for paired sales table
    enableSorting: true,
    state: {
      columnPinning: {
        left: [...getPinnedSystemColumnIds(enableRowSelection), ...pinnedColumnIds],
      },
      rowSelection: makeRowSelectionState(selectedRowIndices),
      sorting,
    },
  });

  // Keep internal selection state in sync
  useEffect(() => {
    if (enableRowSelection) {
      table.setRowSelection(makeRowSelectionState(selectedRowIndices));
    }
  }, [enableRowSelection, selectedRowIndices, table]);

  return table;
}

export { useTPTableCore };
